import type FeatureLayer from '@arcgis/core/layers/FeatureLayer'
// import type MapView from '@arcgis/core/views/MapView'
// import type SceneView from '@arcgis/core/views/SceneView'
import type { WardInfo, WardQueryOptions } from '@/types/ward'
import { loadFeatureLayer } from './lazyArcGIS'

/**
 * Service for managing ward layer operations
 */
export class WardLayerService {
  private wardLayer: FeatureLayer | null = null
  private queryLayer: FeatureLayer | null = null
  // private highlightHandle: __esri.Handle | null = null
  // private multiHighlightHandles: __esri.Handle[] = []
  // private placeHighlightHandle: __esri.Handle | null = null

  /**
   * Initialize the ward layer
   */
  async initialize(featureServiceUrl: string): Promise<void> {
    const { default: FeatureLayer } = await loadFeatureLayer()

    // Create a feature layer specifically for queries
    this.queryLayer = new FeatureLayer({
      url: featureServiceUrl,
      outFields: ['*'],
    })

    // Load the layer to ensure it's ready for queries
    await this.queryLayer.load()

    // For now, use the same layer for display
    // In the future, this could be a separate styled layer
    this.wardLayer = this.queryLayer
  }

  /**
   * Get the ward display layer
   */
  getWardLayer(): FeatureLayer | null {
    return this.wardLayer
  }

  /**
   * Get the query layer
   */
  getQueryLayer(): FeatureLayer | null {
    return this.queryLayer
  }

  /**
   * Query all wards with optional filters
   */
  async queryWards(options?: WardQueryOptions): Promise<WardInfo[]> {
    if (!this.queryLayer) {
      throw new Error('Query layer not initialized')
    }

    const query = this.queryLayer.createQuery()

    // Build where clause with filters
    const whereClauses = ['1=1']

    // if (options?.district) {
    //   whereClauses.push(`quanhuyen = '${options.district}'`)
    // }

    if (options?.areaRange) {
      whereClauses.push(
        `dientichpl >= ${options.areaRange[0]} AND dientichpl <= ${options.areaRange[1]}`
      )
    }

    if (options?.populationRange) {
      whereClauses.push(
        `danso >= ${options.populationRange[0]} AND danso <= ${options.populationRange[1]}`
      )
    }

    query.where = whereClauses.join(' AND ')
    query.outFields = ['*']
    query.returnGeometry = true
    query.orderByFields = ['xaphuong ASC']

    // Add pagination if specified
    if (options?.limit) {
      query.num = options.limit
      query.start = options.offset || 0
    }

    const result = await this.queryLayer.queryFeatures(query)
    return result.features.map((feature) => ({
      ...feature.attributes,
      geometry: feature.geometry,
    })) as WardInfo[]
  }

  /**
   * Query total ward count
   */
  async queryWardsCount(): Promise<number> {
    if (!this.queryLayer) {
      throw new Error('Query layer not initialized')
    }

    const query = this.queryLayer.createQuery()
    query.where = '1=1'
    query.returnGeometry = false

    return await this.queryLayer.queryFeatureCount(query)
  }

  /**
   * Search wards by name
   */
  async searchWards(searchTerm: string): Promise<__esri.Graphic[]> {
    if (!this.queryLayer) {
      throw new Error('Query layer not initialized')
    }

    const query = this.queryLayer.createQuery()
    query.where = `UPPER(xaphuong) LIKE UPPER('%${searchTerm}%')`
    query.returnGeometry = true
    query.outFields = ['*']

    const results = await this.queryLayer.queryFeatures(query)
    return results.features
  }

  /**
   * Query ward by ID
   */
  async queryWardById(objectId: number): Promise<WardInfo | null> {
    if (!this.queryLayer) {
      throw new Error('Query layer not initialized')
    }

    const query = this.queryLayer.createQuery()
    query.where = `OBJECTID = ${objectId}`
    query.returnGeometry = true
    query.outFields = ['*']

    const results = await this.queryLayer.queryFeatures(query)

    if (results.features.length > 0) {
      const feature = results.features[0]
      return {
        ...feature.attributes,
        geometry: feature.geometry,
      } as WardInfo
    }

    return null
  }

  /**
   * Highlight a single ward
   */
  // async highlightWard(view: MapView | SceneView, graphic: __esri.Graphic): Promise<void> {
  //   // if (!this.wardLayer) return
  //
  //   // Remove previous highlight
  //   if (this.highlightHandle) {
  //     this.highlightHandle.remove()
  //     this.highlightHandle = null
  //   }
  //
  //   if (!graphic || !graphic.layer) {
  //     return
  //   }
  //
  //   try {
  //     // Get the layer view and highlight the feature
  //     // const layerView = await view.whenLayerView(this.wardLayer)
  //     const layerView = await view.whenLayerView(graphic.layer as __esri.Layer)
  //     if ('highlight' in layerView) {
  //       // Use objectid if available, otherwise try to use the graphic directly
  //       const objectId = graphic.attributes?.objectid || graphic.attributes?.OBJECTID
  //       if (objectId) {
  //         this.highlightHandle = (layerView.highlight as any)(objectId)
  //       } else {
  //         this.highlightHandle = (layerView.highlight as any)(graphic)
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error highlighting ward:', error)
  //   }
  // }

  // clearWardHighlight() {
  //   if (this.highlightHandle) {
  //     this.highlightHandle.remove()
  //     this.highlightHandle = null
  //   }
  // }

  /**
   * Highlight multiple wards
   */
  // async highlightMultipleWards(view: MapView | SceneView, objectIds: number[]): Promise<void> {
  //   if (!this.wardLayer) return
  //
  //   // Remove all previous multi-highlights
  //   this.clearMultiHighlights()
  //
  //   // If no objectIds, just clear
  //   if (objectIds.length === 0) {
  //     return
  //   }
  //
  //   try {
  //     // Get the layer view and highlight multiple features
  //     const layerView = await view.whenLayerView(this.wardLayer)
  //     const handle = layerView.highlight(objectIds)
  //     this.multiHighlightHandles.push(handle)
  //   } catch (error) {
  //     console.error('Error highlighting multiple wards:', error)
  //   }
  // }

  /**
   * Highlight a place/point feature
   */
  // async highlightPlace(view: MapView | SceneView, graphic: __esri.Graphic): Promise<void> {
  //   // Clear previous place highlight
  //   this.clearPlaceHighlight()
  //
  //   if (!graphic || !graphic.layer) {
  //     return
  //   }
  //
  //   try {
  //     // Get the layer view for the graphic's layer
  //     const layerView = await view.whenLayerView(graphic.layer as __esri.Layer)
  //
  //     // Highlight the specific graphic
  //     if ('highlight' in layerView) {
  //       // Use objectid if available, otherwise try to use the graphic directly
  //       const objectId = graphic.attributes?.objectid || graphic.attributes?.OBJECTID
  //       if (objectId) {
  //         this.placeHighlightHandle = (layerView.highlight as any)(objectId)
  //       } else {
  //         // Some layers support highlighting by graphic
  //         this.placeHighlightHandle = (layerView.highlight as any)(graphic)
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error highlighting place:', error)
  //   }
  // }

  /**
   * Clear place highlight
   */
  // clearPlaceHighlight(): void {
  //   if (this.placeHighlightHandle) {
  //     this.placeHighlightHandle.remove()
  //     this.placeHighlightHandle = null
  //   }
  // }

  /**
   * Clear all highlights
   */
  // clearHighlights(): void {
  //   if (this.highlightHandle) {
  //     this.highlightHandle.remove()
  //     this.highlightHandle = null
  //   }
  //   this.clearMultiHighlights()
  //   this.clearPlaceHighlight()
  // }

  /**
   * Clear multi-selection highlights
   */
  // private clearMultiHighlights(): void {
  //   this.multiHighlightHandles.forEach((handle) => handle.remove())
  //   this.multiHighlightHandles = []
  // }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    // this.clearHighlights()
    this.wardLayer = null
    this.queryLayer = null
  }
}
